/**
 * Language tag parser for editorial content.
 *
 * Supports inline language-specific content using tag syntax:
 *   {python}Python-specific text{/python}
 *   {py}Shorthand works too{/py}
 *
 * Tags can appear inline or span multiple lines.
 * Content outside tags passes through unchanged.
 */

/** Maps shorthand aliases to canonical language names */
const LANGUAGE_ALIASES: Record<string, string> = {
  py: 'python',
  python: 'python',
  js: 'javascript',
  javascript: 'javascript',
  cpp: 'c++',
  'c++': 'c++',
  java: 'java',
  go: 'go',
  golang: 'go',
}

/** All tag names that can appear in {tag}...{/tag} syntax */
const ALL_TAG_NAMES = Object.keys(LANGUAGE_ALIASES)

/** Regex pattern matching any language tag pair (lazy match for content) */
const TAG_PATTERN = new RegExp(
  `\\{(${ALL_TAG_NAMES.map((t) => t.replace('+', '\\+')).join('|')})\\}([\\s\\S]*?)\\{/\\1\\}`,
  'g',
)

/**
 * Parse language-specific tags from content, keeping only content
 * matching the selected language and stripping everything else.
 *
 * @param content - Raw markdown with optional language tags
 * @param language - The language to render for (e.g., "python", "py", "c++", "cpp")
 * @returns Content with matching tags resolved and non-matching tags removed
 */
export function parseLanguageTags(content: string, language: string): string {
  const canonicalLanguage =
    LANGUAGE_ALIASES[language.toLowerCase()] || language.toLowerCase()

  return content.replace(
    TAG_PATTERN,
    (_match, tagName: string, inner: string) => {
      const tagLanguage = LANGUAGE_ALIASES[tagName.toLowerCase()]
      if (tagLanguage === canonicalLanguage) {
        return inner
      }
      return ''
    },
  )
}
